import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { HttpCacheService } from '@app/core/http/http-cache.service';
import { Logger } from '@app/core/logger.service';
import { HypecastAuthenticationService } from '@app/shared/authentication/services/authentication.service';
import { dayjs } from '@app/shared/dayjs-setup';
import { Guid } from '@app/shared/functions/guid';
import { __ } from '@app/shared/functions/object.functions';
import { ApiUrlService } from '@app/shared/services/api-url.service';
import { Response } from '@app/shared/services/local/Response';

import { Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { LoadingDirective } from '../loading/loading.directive';

const lintLogger = new Logger('app/shared/components/image/image.component');

@Component({
  selector: 'hypecast-admin-image',
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LoadingDirective],
})
export class ImageComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly authenticationService = inject(HypecastAuthenticationService);
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly httpCacheService = inject(HttpCacheService);

  readonly loadingContainerClass = input<string>('');
  readonly customClass = input<string>('image');
  readonly loadingClass = input<string>('');
  readonly height = input<string>('auto');
  readonly width = input<string>('auto');
  readonly minHeight = input<string>('initial');
  readonly defaultImage = input<string>('assets/1x1.png');
  readonly src = input<string>();

  readonly isLoaded = output<boolean>();

  readonly isLoading = signal(true);
  readonly currentSrc = signal('assets/1x1.png');

  private secondError: boolean = false;

  constructor() {
    effect((onCleanup) => {
      const subscription = this.loadImage(this.src()!);
      if (subscription) {
        onCleanup(() => subscription.unsubscribe());
      }
    });
  }

  onLoad(): void {
    this.isLoading.set(false);
    this.isLoaded.emit(true);
  }

  onError(): void {
    if (!this.secondError) {
      this.secondError = true;
      this.setImgSrc(this.defaultImage());
    } else {
      this.setImgSrc('assets/1x1.png');
    }
  }

  private loadImage(src: string): Subscription | undefined {
    this.isLoading.set(true);
    this.secondError = false;

    if (__.IsNullOrUndefinedOrEmpty(src)) {
      this.setImgSrc(this.defaultImage());
      return undefined;
    }

    if (this.isGuidLike(src)) {
      return this.fetchPresignedUrlAndSetImage(src);
    }

    this.setImgSrc(src);
    return undefined;
  }

  private setImgSrc(url: string): void {
    this.currentSrc.set(url);
  }

  private isGuidLike(value: string): boolean {
    return !__.IsNullOrUndefinedOrEmpty(value) && value.split('.')[0]!.length === Guid.Empty.length;
  }

  private fetchPresignedUrlAndSetImage(guid: string): Subscription | undefined {
    const requestUrl = `${this.apiUrlService.getCdnUrl()}api/v1/files/${guid}/urls`;
    const cachedResponse = this.httpCacheService.getCacheData(requestUrl) as HttpResponse<Response<string>>;
    if (cachedResponse?.body?.data) {
      lintLogger.debug(`Cache hit for key: ${requestUrl}`);
      this.setImgSrc(cachedResponse.body.data);
      return undefined;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authenticationService.getAccessToken()}`,
      'Cache-Control': 'private, max-age=10000000',
    });

    return this.httpClient
      .get<Response<string>>(requestUrl, {
        context: createHttpContext({ skipApiPrefix: true, skipErrorHandler: true }),
        headers,
        responseType: 'json',
        observe: 'response',
      })
      .pipe(
        tap((res: HttpResponse<Response<string>>) => {
          this.httpCacheService.setCacheData(requestUrl, res, new Date(), dayjs().add(3600, 'second').toDate());
        }),
        map((res: HttpResponse<Response<string>>) => res.body!.data),
      )
      .subscribe({
        next: (presignedUrl: string) => this.setImgSrc(presignedUrl),
        error: () => this.setImgSrc(this.defaultImage()),
      });
  }
}
